// Remote players: cloned character rigs driven by replicated state.
// Each remote gets its own instantiated copy of character.glb plus a LAZY
// clip cache — animation groups are cloned from the local player's already-
// retargeted clips (AnimationGroup.clone with a name-keyed target converter),
// so a remote can play any of the ~120 clips at the cost of only the ones it
// actually uses. Positions interpolate between 10 Hz snapshots with a 150 ms
// render delay; a nameplate billboard hovers over every head.
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { Vector3, Quaternion, Color3 } from '@babylonjs/core/Maths/math';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { DynamicTexture } from '@babylonjs/core/Materials/Textures/dynamicTexture';
import { LerpAngle } from '@babylonjs/core/Maths/math.scalar.functions';
import { Animator } from '../player/animator';
import { buildWeaponProps } from '../player/weapons';

const RENDER_DELAY = 0.15; // s behind newest snapshot

export class RemotePlayers {
  constructor(scene, localClips, shadows) {
    this.scene = scene;
    this.localClips = localClips;
    this.shadows = shadows;
    this.container = null;   // shared source container for instantiation
    this.remotes = new Map(); // id -> remote
    this._loading = new Map();
  }

  async _ensureContainer() {
    if (this.container) return this.container;
    if (!this._containerP) {
      this._containerP = LoadAssetContainerAsync('assets/models/character.glb', this.scene)
        .then((c) => { this.container = c; return c; });
    }
    return this._containerP;
  }

  async add(id, name) {
    if (this.remotes.has(id) || this._loading.has(id)) {
      this.setName(id, name);
      return;
    }
    this._loading.set(id, true);
    try {
      const c = await this._ensureContainer();
      if (this.remotes.has(id)) return; // raced a second add
      const inst = c.instantiateModelsToScene((n) => n, false);
      const root = new TransformNode('remote_' + id, this.scene);
      root.rotationQuaternion = Quaternion.Identity();
      for (const rn of inst.rootNodes) rn.parent = root;
      inst.animationGroups?.forEach((g) => g.stop());

      // name-keyed node map for clip retarget-cloning
      const map = new Map();
      for (const rn of inst.rootNodes) {
        for (const n of [rn, ...rn.getDescendants()]) {
          if (n.name.startsWith('mixamorig:') || n.getClassName?.() === 'Mesh') map.set(n.name, n);
        }
      }
      for (const rn of inst.rootNodes) {
        for (const m of rn.getChildMeshes?.() || []) {
          // pickable + tagged: local hitscan resolves shots on this player
          m.isPickable = true;
          m.metadata = { netPlayer: id };
          this.shadows?.addShadowCaster(m);
        }
      }

      // lazy clip cache masquerading as the Animator's clips object
      const cache = {};
      const localClips = this.localClips;
      const clips = new Proxy(cache, {
        get(t, key) {
          if (typeof key !== 'string') return t[key];
          if (t[key]) return t[key];
          const src = localClips[key];
          if (!src) return undefined;
          const conv = (old) => map.get(old?.name) || old;
          t[key] = {
            L: src.L.clone(`${key}#L@${id}`, conv),
            U: src.U.clone(`${key}#U@${id}`, conv),
            dur: src.dur, loop: src.loop,
          };
          return t[key];
        },
        has(t, key) { return key in t || key in localClips; },
      });
      const animator = new Animator(clips);
      animator.setBase('idle');
      // their weapon, visibly in their hand (same prop builder as the local rig)
      const props = buildWeaponProps(this.scene, map, this.shadows);

      const r = {
        id, name, root, animator, map, props, weapon: 'fists',
        snaps: [],            // [{t, p:[x,y,z], y}]
        yaw: 0, dead: false,
        base: '',             // last base clip name — the only stance signal
        plate: this._nameplate(root, name),
        lastState: null,
      };
      this.remotes.set(id, r);
      root.setEnabled(false); // hidden until the first state lands
    } finally {
      this._loading.delete(id);
    }
  }

  _nameplate(root, name) {
    const plane = MeshBuilder.CreatePlane('plate', { width: 1.1, height: 0.26 }, this.scene);
    plane.parent = root;
    plane.position.y = 2.05;
    plane.billboardMode = Mesh.BILLBOARDMODE_Y;
    plane.isPickable = false;
    const tex = new DynamicTexture('plateTex', { width: 256, height: 60 }, this.scene, true);
    const mat = new StandardMaterial('plateMat', this.scene);
    mat.diffuseTexture = tex;
    mat.emissiveColor = Color3.White();
    mat.disableLighting = true;
    mat.useAlphaFromDiffuseTexture = true;
    tex.hasAlpha = true;
    plane.material = mat;
    this._drawPlate(tex, name);
    return { plane, tex };
  }

  _drawPlate(tex, name) {
    const ctx = tex.getContext();
    ctx.clearRect(0, 0, 256, 60);
    ctx.fillStyle = 'rgba(10,12,9,0.55)';
    ctx.fillRect(0, 6, 256, 48);
    ctx.font = 'bold 30px monospace';
    ctx.fillStyle = '#e8d9a0';
    ctx.textAlign = 'center';
    ctx.fillText(name.slice(0, 16), 128, 40);
    tex.update();
  }

  setName(id, name) {
    const r = this.remotes.get(id);
    if (r && name && r.name !== name) {
      r.name = name;
      this._drawPlate(r.plate.tex, name);
    }
  }

  remove(id) {
    const r = this.remotes.get(id);
    if (!r) return;
    this.remotes.delete(id);
    r.animator.base = null;
    r.root.dispose(false, true);
  }

  // 10 Hz state from the server
  applyState(id, s) {
    const r = this.remotes.get(id);
    if (!r || !s?.p) return;
    r.lastState = s;
    r.snaps.push({ t: performance.now() / 1000, p: s.p, y: s.y || 0 });
    if (r.snaps.length > 12) r.snaps.shift();
    if (!r.root.isEnabled()) {
      r.root.setEnabled(true);
      r.root.position.set(s.p[0], s.p[1], s.p[2]);
      r.yaw = s.y || 0;
    }
    // animation by name — full fidelity, clips clone on demand
    if (s.d && !r.dead) { r.dead = true; r.animator.die(s.dn || 'death'); }
    else if (!s.d && r.dead) { r.dead = false; r.animator.revive(); r.animator.setBase(s.b || 'idle', s.r || 1); }
    if (!r.dead) {
      if (s.b) r.animator.setBase(s.b, s.r || 1);
      r.animator.setHold(s.h || null);
    }
    // Keep the base clip name, not just hand it to the animator: it is the
    // only stance signal on the wire (the packet carries no stance field, and
    // the server reads the same `b` for its gait-noise table). main.js's
    // bodiesNear source needs it so a crouched or prone player is not
    // published to the world query as a full 1.8 m standing cylinder — you
    // could hit a prone player by shooting the air a metre above them.
    if (s.b) r.base = s.b;
    if (s.w && s.w !== r.weapon) {
      r.weapon = s.w;
      r.props.setActive(s.w === 'fists' ? null : s.w);
    }
  }

  // one-shot attack/reload/swing animations, replicated by clip name
  applySwing(id, { fn, name, rate }) {
    const r = this.remotes.get(id);
    if (!r || r.dead || !name) return;
    if (fn === 'playFull') r.animator.playFull(name, rate || 1);
    else r.animator.playUpper(name, rate || 1);
  }

  update(dt) {
    const now = performance.now() / 1000 - RENDER_DELAY;
    for (const r of this.remotes.values()) {
      const sn = r.snaps;
      if (!sn.length || !r.root.isEnabled()) continue;
      // find the pair bracketing render-time
      let a = sn[0], b = sn[sn.length - 1];
      for (let i = sn.length - 1; i > 0; i--) {
        if (sn[i - 1].t <= now) { a = sn[i - 1]; b = sn[i]; break; }
      }
      const span = Math.max(0.001, b.t - a.t);
      const k = Math.max(0, Math.min(1.2, (now - a.t) / span)); // slight extrapolation
      const px = a.p[0] + (b.p[0] - a.p[0]) * k;
      const py = a.p[1] + (b.p[1] - a.p[1]) * k;
      const pz = a.p[2] + (b.p[2] - a.p[2]) * k;
      if (Math.hypot(px - r.root.position.x, pz - r.root.position.z) > 25) {
        r.root.position.set(px, py, pz); // teleport — don't glide across the map
      } else {
        r.root.position.set(px, py, pz);
      }
      r.yaw = LerpAngle(r.yaw, b.y, Math.min(1, dt * 12));
      Quaternion.RotationAxisToRef(Vector3.UpReadOnly, r.yaw, r.root.rotationQuaternion);
      r.animator.update(dt);
    }
  }

  count() { return this.remotes.size; }
  positions() {
    const out = [];
    for (const r of this.remotes.values()) if (r.root.isEnabled()) out.push(r.root.position);
    return out;
  }
}
